import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function formatDateTime(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function AdminMockExamAttempts() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [passFail, setPassFail] = useState('');
  const [evaluationStatus, setEvaluationStatus] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const params = {};
      if (studentSearch) params.studentSearch = studentSearch;
      if (passFail) params.passFail = passFail;
      if (evaluationStatus) params.evaluationStatus = evaluationStatus;
      const response = await axios.get(`${apiUrl}/api/admin/mock-exams/${examId}/attempts`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (response.data.success) setAttempts(response.data.attempts || []);
      else setError(response.data.message || 'Failed to fetch attempts.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching attempts.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, studentSearch, passFail, evaluationStatus]);

  useEffect(() => {
    const timer = setTimeout(() => fetchAttempts(), 250);
    return () => clearTimeout(timer);
  }, [fetchAttempts]);

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-mock-exams" className="text-gray-200 fw-normal text-15 hover-text-main-600">Mock Exams</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Attempts</span></li>
              </ul>
            </div>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Student name / ID</label>
                  <input type="text" className="form-control py-9" value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Pass / Fail</label>
                  <select className="form-control py-9" value={passFail} onChange={(e) => setPassFail(e.target.value)}>
                    <option value="">All</option>
                    <option value="pass">Pass</option>
                    <option value="fail">Fail</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Evaluation Status</label>
                  <select className="form-control py-9" value={evaluationStatus} onChange={(e) => setEvaluationStatus(e.target.value)}>
                    <option value="">All</option>
                    <option value="pending">Pending Evaluation</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Attempts ({attempts.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : attempts.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No attempts found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Student ID</th>
                        <th>Attempt #</th>
                        <th>Started</th>
                        <th>Submitted</th>
                        <th>Marks</th>
                        <th>Result</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attempts.map((a) => (
                        <tr key={a.id}>
                          <td>{a.firstName} {a.lastName}</td>
                          <td>{a.studentid}</td>
                          <td>{a.attempt_number}</td>
                          <td>{formatDateTime(a.started_at)}</td>
                          <td>{formatDateTime(a.submitted_at)}</td>
                          <td>{a.total_marks_scored !== null ? `${a.total_marks_scored} / ${a.total_marks_possible}` : '—'}</td>
                          <td>
                            {a.pass_fail ? (
                              <span className={`badge ${a.pass_fail === 'pass' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>{a.pass_fail}</span>
                            ) : '—'}
                          </td>
                          <td>
                            <span className="badge bg-neutral-200 text-neutral-600">{a.status.replace('_', ' ')}</span>
                          </td>
                          <td>
                            <button type="button" className="btn btn-sm btn-outline-main py-4 px-8" onClick={() => navigate(`/admin-mock-exam-attempt/${a.id}`)}>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExamAttempts;
